import { WEBSITE_ID_KEY } from "app/components/dashboard/widget-wizard/components/segments/constants";
import type { SingleSegmentSeriesType } from "app/components/widget/widget-utilities/segments/types";
import type { ICustomSegmentsGroupAnalysisParams } from "app/services/segments/segmentsApiService";
import { granularities } from "pro-features/utils";

import markerWithDashedConfig from "pro-features/components/Chart/src/configs/series/markerWithDashedLinePerPointChartConfig";
import type { ChartType, DashboardMetric } from "../type";

import { SegmentsGroupMonthlyVisitsGraphWidget } from "./SegmentsGroupMonthlyVisitsGraphWidget";

export class SegmentsUniqueVisitorsGroupGraphWidget extends SegmentsGroupMonthlyVisitsGraphWidget {
    protected metric: DashboardMetric;
    protected dataSource;

    constructor() {
        super();
        this.dataSource = "UniqueUsers";
        this.metric = "UniqueVisitsGroup";
    }

    static getWidgetDashboardType() {
        return "Graph";
    }

    static getWidgetResourceType() {
        return "Graph";
    }

    static getWidgetMetadataType() {
        return "SegmentsUniqueVisitorsGroupGraph";
    }

    public fetchSegmentData = async () => {
        const { segmentsGroupId, selectedSegmentsId, country, webSource } = this._params;
        const timeGranularity = this.durationObject?.forAPI?.isWindow ? granularities[0] : granularities[2];
        const segmentGroupId = segmentsGroupId || this._widgetConfig.segmentsGroupId;

        const apiParams = {
            compareFrom: this.durationObject?.forAPI?.compareFrom,
            compareTo: this.durationObject?.forAPI?.compareTo,
            segmentGroupId,
            country,
            from: this.durationObject.forAPI.from,
            to: this.durationObject.forAPI.to,
            webSource,
            isWindow: this.durationObject?.forAPI?.isWindow,
            includeSubDomains: true,
            timeGranularity,
            keys: "",
            lastUpdated: this.currentSegmentsData[0].lastUpdated,
        } as ICustomSegmentsGroupAnalysisParams;

        if (webSource === "Total") {
            const totalResponse = await this.segmentApiService.getCustomSegmentEngagementData(
                //@ts-ignore
                apiParams,
            );
            const result = {};

            selectedSegmentsId.forEach((segmentId: string) => {
                const uniqueUsersGraph = totalResponse[segmentId]?.UniqueUsers[timeGranularity]?.Graph;

                result[segmentId] = uniqueUsersGraph.map((item) => ({
                    Key: item.Key,
                    Value: {
                        Value: item?.Value?.Value,
                        Confidence: item?.Value?.Confidence,
                    },
                }));
            });

            return result;
        }

        return Promise.all([
            this.segmentApiService.getCustomSegmentsGroupAnalysis(apiParams),
            //@ts-ignore
            this.segmentApiService.getUniqueUsersData(apiParams),
        ]).then((res) => {
            const result = {};

            selectedSegmentsId.forEach((segmentId: string) => {
                const uniqueUsersGraph = res[1][segmentId]?.UniqueUsers?.Monthly?.Graph;
                const visitsGraph = res[0][segmentId]?.Visits?.Monthly?.Graph;

                result[segmentId] = uniqueUsersGraph.map((item, index) => ({
                    Key: item.Key,
                    Value: {
                        Value: item?.Value,
                        Confidence: visitsGraph[index]?.Value?.Confidence,
                    },
                }));
            });

            return result;
        });
    };

    public async getChartSeries(data: ChartType): Promise<SingleSegmentSeriesType[]> {
        const { selectedSegmentsId } = this._params;

        const result: SingleSegmentSeriesType[] = [];

        if (data) {
            const segmentsMeta = new Map();

            this.currentSegmentsData.forEach((item) => {
                segmentsMeta.set(item.id, item);
            });

            selectedSegmentsId.forEach((segmentId: string) => {
                const dataById = data?.[segmentId];

                if (dataById) {
                    const isWebsite = segmentId.includes(WEBSITE_ID_KEY);

                    const formattedChartData = this.getFormattedChartData(dataById);
                    const currentSegmentMetaData = isWebsite
                        ? { domain: segmentId.replace(WEBSITE_ID_KEY, ""), isWebsite: true }
                        : segmentsMeta.get(segmentId);

                    const series = markerWithDashedConfig({
                        data: formattedChartData,
                        isDataSingleSeries: true,
                    }) as SingleSegmentSeriesType;

                    series.name = currentSegmentMetaData?.segmentName;
                    series.displayName = currentSegmentMetaData?.domain;
                    series.isWebsite = currentSegmentMetaData?.isWebsite;

                    result.push(series);
                }
            });
        }

        return result;
    }
}

SegmentsUniqueVisitorsGroupGraphWidget.register();
